import { useState, useCallback, useEffect } from 'react';
import { groqService, PromptToken, ConflictAnalysis, EnhancedPrompt } from '@/services/groqService';

export interface PromptEngineState {
  tokens: PromptToken[];
  conflicts: ConflictAnalysis | null;
  isAnalyzing: boolean;
  isEnhancing: boolean;
  enhancedPrompt: EnhancedPrompt | null;
  error: string | null;
}

export const usePromptEngine = (initialPrompt?: string) => {
  const [state, setState] = useState<PromptEngineState>({
    tokens: [],
    conflicts: null,
    isAnalyzing: false,
    isEnhancing: false,
    enhancedPrompt: null,
    error: null,
  });

  // Debounce function
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // Process initial prompt
  const processInitialPrompt = useCallback(async (description: string) => {
    setState(prev => ({ ...prev, isAnalyzing: true, error: null }));
    
    try {
      const tokens = await groqService.processInitialPrompt(description);
      setState(prev => ({ 
        ...prev, 
        tokens, 
        isAnalyzing: false 
      }));
      
      // Analyze conflicts after processing
      analyzeConflicts(tokens);
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isAnalyzing: false, 
        error: error instanceof Error ? error.message : 'Failed to process prompt' 
      }));
    }
  }, []);

  // Analyze conflicts
  const analyzeConflicts = useCallback(async (tokens: PromptToken[]) => {
    setState(prev => ({ ...prev, isAnalyzing: true, error: null }));
    
    try {
      const conflicts = await groqService.analyzeConflicts(tokens);
      
      // Update tokens with conflict information
      const updatedTokens = tokens.map(token => ({
        ...token,
        hasConflict: conflicts.conflicts.some(c => c.tokenId === token.id),
        suggestion: conflicts.conflicts.find(c => c.tokenId === token.id)?.suggestion
      }));
      
      setState(prev => ({ 
        ...prev, 
        tokens: updatedTokens,
        conflicts, 
        isAnalyzing: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isAnalyzing: false, 
        error: error instanceof Error ? error.message : 'Failed to analyze conflicts' 
      }));
    }
  }, []);

  // Enhance prompt
  const enhancePrompt = useCallback(async () => {
    if (state.tokens.length === 0) return;
    
    setState(prev => ({ ...prev, isEnhancing: true, error: null }));
    
    try {
      const enhanced = await groqService.enhancePrompt(state.tokens);
      setState(prev => ({ 
        ...prev, 
        enhancedPrompt: enhanced,
        isEnhancing: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isEnhancing: false, 
        error: error instanceof Error ? error.message : 'Failed to enhance prompt' 
      }));
    }
  }, [state.tokens]);

  // Update token
  const updateToken = useCallback((tokenId: string, updates: Partial<PromptToken>) => {
    setState(prev => ({
      ...prev,
      tokens: prev.tokens.map(token => 
        token.id === tokenId ? { ...token, ...updates } : token
      )
    }));
  }, []);

  // Apply quick fix
  const applyQuickFix = useCallback((tokenId: string) => {
    const token = state.tokens.find(t => t.id === tokenId);
    if (token?.suggestion) {
      updateToken(tokenId, { text: token.suggestion, hasConflict: false });
      // Re-analyze conflicts after fix
      const updatedTokens = state.tokens.map(t => 
        t.id === tokenId ? { ...t, text: token.suggestion!, hasConflict: false } : t
      );
      analyzeConflicts(updatedTokens);
    }
  }, [state.tokens, updateToken, analyzeConflicts]);

  // Get prompt text
  const getPromptText = useCallback(() => {
    return state.tokens.map(t => t.text).join(' ');
  }, [state.tokens]);

  // Debounced conflict analysis
  const debouncedAnalysis = useCallback(
    debounce(analyzeConflicts, 1000),
    [analyzeConflicts]
  );

  // Update tokens and trigger analysis
  const updateTokens = useCallback((tokens: PromptToken[]) => {
    setState(prev => ({ ...prev, tokens }));
    debouncedAnalysis(tokens);
  }, [debouncedAnalysis]);

  // Initialize with initial prompt
  useEffect(() => {
    if (initialPrompt) {
      processInitialPrompt(initialPrompt);
    }
  }, [initialPrompt, processInitialPrompt]);

  return {
    // State
    ...state,
    
    // Actions
    processInitialPrompt,
    analyzeConflicts,
    enhancePrompt,
    updateToken,
    updateTokens,
    applyQuickFix,
    getPromptText,
    
    // Computed values
    hasConflicts: state.conflicts?.conflicts.length > 0,
    isConsistent: state.conflicts?.isConsistent ?? true,
    enhancedText: state.enhancedPrompt?.enhancedText,
  };
};
